import { Input, Node, Output } from "rete"
import { NodeData, WorkerInputs, WorkerOutputs } from "rete/types/core/data"
import { BooleanTileGrid, CategoricalTileGrid } from "../tile_grid"
import { booleanDataSocket, categoricalDataSocket } from "../socket_types"
import { BaseComponent } from "./base_component"
import { getCategoricalLabelsFromInputChain } from "./categorical_label_chain"
import {
  isCategoricalChainSeedingSuppressed,
  isLabelOutputReconnectInProgressForNode,
  mergeLabelOutputWireSnapshotsForReconnect,
  type LabelOutputWireSnapshot,
  reconnectLabelOutputsByNormalizedLabel,
  rememberLabelOutputWireSnapshots,
  runWithLabelOutputReconnect,
  snapshotLabelOutputConnections,
  takePendingLabelOutputWireSnapshots,
} from "./categorical_dynamic_io_connections"

export class CategoricalToBooleanComponent extends BaseComponent {
  private static connectionHooksByNodeId = new Set<number>()

  constructor() {
    super("Categorical to boolean datasets")
    this.category = "Conversions"
    this.contextMenuName = "Categorical to boolean datasets"
  }

  async builder(node: Node) {
    node.addInput(new Input("in", "Categorical dataset", categoricalDataSocket))

    const savedLabels = node.data.splitLabels as Array<{ value: number; label: string }> | undefined
    const hasSavedLabels = Array.isArray(savedLabels) && savedLabels.length > 0
    if (hasSavedLabels) {
      this.syncBooleanOutputs(node, savedLabels!.map(({ value, label }) => [value, label]))
    } else {
      // No saved label set — derive one from the upstream chain. Skipped
      // when saved labels already provided the outputs (JSON replay will
      // restore matching connections by key).
      this.seedOutputsFromConnections(node)
    }

    if (this.editor && !CategoricalToBooleanComponent.connectionHooksByNodeId.has(node.id)) {
      CategoricalToBooleanComponent.connectionHooksByNodeId.add(node.id)
      const refreshOnConnectionChange = () => {
        // Skip during `editor.fromJSON` — see categorical_dynamic_io_connections
        // for the rationale (O(N*M*D) chain walks otherwise).
        if (isCategoricalChainSeedingSuppressed()) return
        this.seedOutputsFromConnections(node)
      }
      this.editor.on("connectioncreated", refreshOnConnectionChange)
      this.editor.on("connectionremoved", refreshOnConnectionChange)
      this.editor.on("noderemoved", (removedNode: any) => {
        if (removedNode?.id === node.id) {
          CategoricalToBooleanComponent.connectionHooksByNodeId.delete(node.id)
        }
      })
    }
  }

  private outputKeyForLabel(value: number) {
    return `label_${value}`
  }

  private syncBooleanOutputs(node: Node, labels: Array<[number, string]>, snapsOverride?: LabelOutputWireSnapshot[]) {
    const snaps =
      snapsOverride !== undefined
        ? snapsOverride
        : this.editor != null
          ? snapshotLabelOutputConnections(node, k => k.startsWith("label_"))
          : []

    const getExistingOutput = (key: string) => {
      const outputsAny = node.outputs as any
      if (typeof outputsAny?.get === "function") return outputsAny.get(key)
      return outputsAny?.[key]
    }

    const desired = new Map<string, string>()
    labels
      .filter(([value]) => value !== 255)
      .sort((a, b) => a[0] - b[0])
      .forEach(([value, label]) => {
        desired.set(this.outputKeyForLabel(value), label)
      })

    // Remove stale per-label outputs when upstream label set shrinks.
    // Keep only dynamic outputs with key pattern `label_<value>`.
    Array.from(node.outputs.values()).forEach((output: any) => {
      if (!String(output.key).startsWith("label_")) return
      if (desired.has(output.key)) return
      ;[...output.connections].forEach(c => this.editor?.removeConnection(c))
      node.removeOutput(output)
    })

    desired.forEach((label, key) => {
      const existing = getExistingOutput(key)
      if (existing) {
        existing.name = label
      } else {
        node.addOutput(new Output(key, label, booleanDataSocket))
      }
    })

    if (this.editor) {
      runWithLabelOutputReconnect(node, () =>
        reconnectLabelOutputsByNormalizedLabel(this.editor!, node, snaps, k => k.startsWith("label_"))
      )
    }
  }

  private pruneOutputsWithoutLabels(node: Node) {
    if (this.editor) {
      rememberLabelOutputWireSnapshots(
        node,
        snapshotLabelOutputConnections(node, k => k.startsWith("label_"))
      )
    }
    Array.from(node.outputs.values()).forEach((output: any) => {
      if (!String(output.key).startsWith("label_")) return
      ;[...output.connections].forEach(c => this.editor?.removeConnection(c))
      node.removeOutput(output)
    })
  }

  private seedOutputsFromConnections(node: Node) {
    if (!this.editor) return
    if (isLabelOutputReconnectInProgressForNode(node.id)) return
    const rows = getCategoricalLabelsFromInputChain(this.editor, node, "in")
    if (rows.length === 0) {
      delete node.data.splitLabels
      this.pruneOutputsWithoutLabels(node)
      node.update()
      return
    }
    node.data.splitLabels = rows
    const pending = takePendingLabelOutputWireSnapshots(node)
    const live =
      this.editor != null ? snapshotLabelOutputConnections(node, k => k.startsWith("label_")) : []
    const merged = mergeLabelOutputWireSnapshotsForReconnect(live, pending)
    this.syncBooleanOutputs(node, rows.map(({ value, label }) => [value, label]), merged)
    node.update()
  }

  worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs) {
    const editorNode = this.editor?.nodes.find(n => n.id === node.id)
    if (!editorNode) return

    const input = inputs["in"]?.[0]
    if (!(input instanceof CategoricalTileGrid)) {
      editorNode.meta.errorMessage = "Requires categorical input"
      delete editorNode.data.splitLabels
      this.pruneOutputsWithoutLabels(editorNode)
      editorNode.update()
      return
    }
    delete editorNode.meta.errorMessage

    const labelEntries = Array.from(input.labels?.entries() ?? [])
      .filter(([value]) => value !== 255)
      .sort((a, b) => a[0] - b[0])

    editorNode.data.splitLabels = labelEntries.map(([value, label]) => ({ value, label }))
    this.syncBooleanOutputs(editorNode, labelEntries)

    labelEntries.forEach(([value, label]) => {
      const outputKey = this.outputKeyForLabel(value)
      const labelOutput = (editorNode.outputs as any).get?.(outputKey) ?? (editorNode.outputs as any)[outputKey]
      if (!labelOutput) return

      const boolOut = new BooleanTileGrid(input.zoom, input.x, input.y, input.width, input.height, false)
      input.iterate((x, y, categoryValue) => {
        if (categoryValue === value) {
          boolOut.set(x, y, true)
        }
      })
      boolOut.name = label
      outputs[outputKey] = boolOut
    })

    editorNode.update()
  }
}
